import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { BaseTypeTier, WeaponType, ArmourType, DefenceType, DisplayType, EquipmentTierType, GearSizeType, GearBaseTypeMode } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { GearTierService } from "../tiering/gear-tier.service";
import { JewelleryTierService } from "../tiering/jewellery-tier.service";
import { Equipment, Tier, TieredGear, BaseType, GearTierType, JewelleryTierType } from "../../models/item-data-models";
import { CosmeticOptions, DisplayOptions } from "../../models/filter-models";
import { EquipmentTierService } from "../tiering/equipment-tier.service";
import { FilterStoreService } from "../filter-store.service";
import { AutocompleteOption } from "../../common/autocomplete/autocomplete.component";

const GEAR_TIER_DESC_ORDER: GearTierType[] = ['Master', 'Elite', 'Expert', 'Advanced', 'Basic'];

export function sortAutocompleteOptionsByGearTierDesc(options: AutocompleteOption[]): AutocompleteOption[] {
  return [...options].sort((a, b) => {
    const tierDiff = GEAR_TIER_DESC_ORDER.indexOf(a.gearTier ?? 'Basic') - GEAR_TIER_DESC_ORDER.indexOf(b.gearTier ?? 'Basic');
    return tierDiff !== 0 ? tierDiff : a.name.localeCompare(b.name);
  });
}

const JEWELLERY_TIER_DESC_ORDER: JewelleryTierType[] = ['Excellent', 'Good', 'Special', 'Other'];

export function sortAutocompleteOptionsByJewelleryTierDesc(options: AutocompleteOption[]): AutocompleteOption[] {
  return [...options].sort((a, b) => {
    const tierDiff = JEWELLERY_TIER_DESC_ORDER.indexOf(a.jewelleryTier ?? 'Other') - JEWELLERY_TIER_DESC_ORDER.indexOf(b.jewelleryTier ?? 'Other');
    return tierDiff !== 0 ? tierDiff : a.name.localeCompare(b.name);
  });
}

@Injectable({
  providedIn: 'root'
})
export class GenerationHelperService {
  constructor(private itemDataService: ItemDataService, private gearTierService: GearTierService, private jewelleryTierService: JewelleryTierService,
    private equipmentTierService: EquipmentTierService, private filterStoreService: FilterStoreService) { }

  // All tiered gear base names matching selected weapon classes and/or armour slots + defence (no tier filter).
  resolveGearRuleEligibleBaseTypeNames(weaponType?: WeaponType[], armourType?: ArmourType[], defenceTypes?: DefenceType[]): string[] {
    return this.filterTieredGearByClassAndDefence(weaponType, armourType, defenceTypes).map(g => g.name);
  }

  // Base types for my-weapon / my-armour rules: either tier slice or intersection of user picks with eligibility.
  resolveMyGearEmittedBaseTypes(
    baseTypeMode: GearBaseTypeMode,
    baseTypeTier: BaseTypeTier,
    specificBaseTypes: string[],
    weaponType?: WeaponType[],
    armourType?: ArmourType[],
    defenceTypes?: DefenceType[],
    includeHigherTiers = true,
  ): string[] {
    if (baseTypeMode === GearBaseTypeMode.Specific) {
      if (specificBaseTypes.includes('All')) {
        // Armour "All" must still respect defence type (same as Tier/Basic); weapons use class filter only.
        if (armourType && armourType.length > 0) {
          return this.resolveGearRuleEligibleBaseTypeNames(weaponType, armourType, defenceTypes);
        }
        return ['All'];
      }
      const eligible = new Set(this.resolveGearRuleEligibleBaseTypeNames(weaponType, armourType, defenceTypes));
      return [...new Set(specificBaseTypes)].filter(bt => eligible.has(bt));
    }
    return this.buildBaseTypesForTier(baseTypeTier, weaponType, armourType, defenceTypes, includeHigherTiers);
  }

  // Autocomplete options (name + image + gear tier) for my gear base-type picker.
  getMyGearBaseTypeAutocompleteOptions(weaponType?: WeaponType[], armourType?: ArmourType[], defenceTypes?: DefenceType[]): AutocompleteOption[] {
    const tieredGear = this.filterTieredGearByClassAndDefence(weaponType, armourType, defenceTypes);
    const imageByName = new Map(
      this.collectBaseTypeOptionsFromItemData(new Set(tieredGear.map(g => g.name))).map(bt => [bt.name, bt.image]),
    );
    const options: AutocompleteOption[] = tieredGear.map(g => ({
      name: g.name,
      image: imageByName.get(g.name),
      gearTier: g.tier,
    }));
    return sortAutocompleteOptionsByGearTierDesc(options);
  }

  // Autocomplete options (name + image + jewellery tier) for my jewellery base-type picker.
  getMyJewelleryBaseTypeAutocompleteOptions(jewelleryType: string): AutocompleteOption[] {
    const tieredJewellery = this.jewelleryTierService.getJewelleryTierData().filter(j => j.itemClass === jewelleryType);
    if (tieredJewellery.length === 0) {
      const itemData = this.itemDataService.getItemData().find(i => i.itemType === jewelleryType);
      return itemData?.baseTypes.map(bt => ({ name: bt.name, image: bt.image })) ?? [];
    }
    const imageByName = new Map(
      this.collectBaseTypeOptionsFromItemData(new Set(tieredJewellery.map(j => j.name))).map(bt => [bt.name, bt.image]),
    );
    const options: AutocompleteOption[] = tieredJewellery.map(j => ({
      name: j.name,
      image: imageByName.get(j.name),
      jewelleryTier: j.tier,
    }));
    return sortAutocompleteOptionsByJewelleryTierDesc(options);
  }

  buildBaseTypesForTier(baseTypeTier: BaseTypeTier, weaponType?: WeaponType[], armourType?: ArmourType[], defenceTypes?: DefenceType[], includeHigherTiers = true): string[] {
    const gearOfClass = this.filterTieredGearByClassAndDefence(weaponType, armourType, defenceTypes);

    const applicableBaseTypeTiers = baseTypeTier === BaseTypeTier.Basic ? ['Basic', 'Advanced', 'Expert', 'Elite', 'Master'] :
      baseTypeTier === BaseTypeTier.Advanced ? ['Advanced', 'Expert', 'Elite', 'Master'] :
      baseTypeTier === BaseTypeTier.Expert ? ['Expert', 'Elite', 'Master'] :
      baseTypeTier === BaseTypeTier.Elite ? ['Elite', 'Master'] :
      baseTypeTier === BaseTypeTier.Master ? ['Master'] : [];

    const gear = gearOfClass.filter(g => includeHigherTiers ? applicableBaseTypeTiers.includes(g.tier) : g.tier === baseTypeTier.toString());

    return gear.map(g => g.name);
  }

  private filterTieredGearByClassAndDefence(weaponType?: WeaponType[], armourType?: ArmourType[], defenceTypes?: DefenceType[]): TieredGear[] {
    const allWeapons = this.itemDataService.getAllWeaponTypes();
    const allArmour = this.itemDataService.getAllArmourTypes();

    let gear = this.gearTierService
      .getGearTierData()
      .filter(g =>
        (weaponType?.includes(WeaponType.All) && allWeapons.includes(g.itemClass))
        || (armourType?.includes(ArmourType.All) && allArmour.includes(g.itemClass))
        || (weaponType && weaponType.includes(g.itemClass as WeaponType))
        || (armourType && armourType.includes(g.itemClass as ArmourType)));

    if (defenceTypes && defenceTypes.length > 0 && defenceTypes[0] !== 'All') {
      gear = gear.filter(g => defenceTypes.some(dt => this.tieredGearMatchesDefenceType(g, dt)));
    }

    return gear;
  }

  private tieredGearMatchesDefenceType(g: TieredGear, dt: DefenceType): boolean {
    return (dt === DefenceType.Armour && g.defenceType === DefenceType.Armour) ||
      (dt === DefenceType.Evasion && g.defenceType === DefenceType.Evasion) ||
      (dt === DefenceType.EnergyShield && g.defenceType === DefenceType.EnergyShield) ||
      (dt === DefenceType.ArmourEnergyShield && g.defenceType === DefenceType.ArmourEnergyShield) ||
      (dt === DefenceType.ArmourEvasion && g.defenceType === DefenceType.ArmourEvasion) ||
      (dt === DefenceType.EvasionEnergyShield && g.defenceType === DefenceType.EvasionEnergyShield);
  }

  private collectBaseTypeOptionsFromItemData(eligibleNames: Set<string>): BaseType[] {
    const byName = new Map<string, BaseType>();
    for (const item of this.itemDataService.getItemData()) {
      for (const bt of item.baseTypes) {
        if (eligibleNames.has(bt.name) && !byName.has(bt.name)) {
          byName.set(bt.name, bt);
        }
      }
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  formatDefenceType(defenceTypes: DefenceType[]) {
    let canContainArmour = false, canContainEnergyShield = false, canContainEvasion = false;

    for (const defenceType of defenceTypes) {
      if (defenceType === DefenceType.Armour || defenceType === DefenceType.ArmourEnergyShield || defenceType === DefenceType.ArmourEvasion) {
        canContainArmour = true;
      }
      if (defenceType === DefenceType.EnergyShield || defenceType === DefenceType.EvasionEnergyShield || defenceType === DefenceType.ArmourEnergyShield) {
        canContainEnergyShield = true;
      }
      if (defenceType === DefenceType.Evasion || defenceType === DefenceType.ArmourEvasion || defenceType === DefenceType.EvasionEnergyShield) {
        canContainEvasion = true;
      }
    }

    let filter = '';

    if (!canContainArmour) filter += '\n  BaseArmour == 0';
    if (!canContainEnergyShield) filter += '\n  BaseEnergyShield == 0';
    if (!canContainEvasion) filter += '\n  BaseEvasion == 0';

    return filter;
  }

  buildWeaponClass(w: WeaponType[]): string {
    if (w.length === 0) return '';
    return w.includes(WeaponType.All)
      ? this.itemDataService.getAllWeaponTypes().map(g => `"${g}"`).join(' ')
      : w.map(g => `"${g}"`).join(' ');
  }

  buildArmourClass(w: ArmourType[]): string {
    if (w.length === 0) return '';
    return w.includes(ArmourType.All)
      ? this.itemDataService.getAllArmourTypes().map(g => `"${g}"`).join(' ')
      : w.map(g => `"${g}"`).join(' ');
  }

  getEquipmentTier(type: EquipmentType): Tier {
    return this.equipmentTierService.getEquipmentTier(type);
  }

  getEquipmentTierDisplayOptionsByType(equipmentType: EquipmentType, active?: boolean, ignoreCustomRuleStyle = false): DisplayOptions {
    const equipment = this.equipmentTierService.getMetadataForEquipmentType(equipmentType);
    if (!equipment) return new DisplayOptions();
    return this.getEquipmentTierDisplayOptions(equipment, active, ignoreCustomRuleStyle);
  }

  getEquipmentTierDisplayOptions(equipment: Equipment, active?: boolean, ignoreCustomRuleStyle = false): DisplayOptions {
    if (active === false) {
      // Only when false was explicitly passed as active; not just unspecified
      if (equipment?.hiddenWhenDisabled) return new DisplayOptions({ hidden: true });
      if (equipment?.visibleWhenDisabled) return new DisplayOptions();
    }

    if (!ignoreCustomRuleStyle) {
      const customRuleStyle = this.filterStoreService.activeFilter().customRuleStyles.find(s => s.ruleId === equipment.ruleId);
      if (customRuleStyle) {
        return new DisplayOptions({
          displayType: DisplayType.Custom,
          cosmeticOptions: new CosmeticOptions({ ...customRuleStyle.options }),
        });
      }
    }

    const displayTypeTier = this.getEquipmentTier(equipment.equipmentType);
    let displayTypeTierHighlightType: EquipmentTierType = EquipmentTierType.Item;

    if (equipment) {
      if (equipment.selection === 'gems') {
        displayTypeTierHighlightType = EquipmentTierType.Gem;
      } else if (equipment.selection === 'maps') {
        displayTypeTierHighlightType = EquipmentTierType.Map;
      } else if (equipment.selection === 'my') {
        displayTypeTierHighlightType = EquipmentTierType.MyEquipment;
      } else if (equipment.category === 'identified') {
        displayTypeTierHighlightType = EquipmentTierType.IdentifiedEquipment;
      } else if (equipment.category === 'rare' || equipment.category === 'magic') {
        displayTypeTierHighlightType = EquipmentTierType.UnidentifiedEquipment;
      } else if (equipment.category === 'leveling') {
        displayTypeTierHighlightType = EquipmentTierType.LevelingEquipment;
      } else if (equipment.category === 'salvaging') {
        displayTypeTierHighlightType = EquipmentTierType.SalvagingEquipment;
      } else if (equipment.category === 'crafting') {
        displayTypeTierHighlightType = EquipmentTierType.CraftingBaseEquipment;
      } else {
        displayTypeTierHighlightType = EquipmentTierType.Item;
      }
    }

    if (equipment.equipmentType === EquipmentType.NormalGoldStacks || equipment.equipmentType === EquipmentType.LargeGoldStacks || equipment.equipmentType === EquipmentType.HugeGoldStacks) {
      displayTypeTierHighlightType = EquipmentTierType.Gold;
    }

    if (equipment.equipmentType === EquipmentType.NormalVerisiumStacks || equipment.equipmentType === EquipmentType.LargeVerisiumStacks || equipment.equipmentType === EquipmentType.HugeVerisiumStacks) {
      displayTypeTierHighlightType = EquipmentTierType.Verisium;
    }

    return new DisplayOptions({
      displayType: DisplayType.EquipmentTier,
      displayTypeTierHighlightType,
      displayTypeTier,
    });
  }

  getSizesByGearSizeType(gearSizeType: GearSizeType): string[] {
    switch (gearSizeType) {
      case GearSizeType.MediumOnly: return [`
  Width <= 2
  Height <= 3`];
      case GearSizeType.SmallOnly: return [`
  Width <= 2
  Height <= 2`,`
  Width <= 1
  Height <= 4`];
      case GearSizeType.TwoByTwoOnly: return [`
  Width <= 2
  Height <= 2`];
      case GearSizeType.JewelleryOnly: return [`
  Height == 1`];
      default: return [`
  Width <= 2
  Height <= 4`];
    }
  }

  getSizeDescription(gearSizeType: GearSizeType): string {
    switch (gearSizeType) {
      case GearSizeType.MediumOnly: return 'Medium';
      case GearSizeType.SmallOnly: return 'Small';
      case GearSizeType.TwoByTwoOnly: return '2x2';
      case GearSizeType.JewelleryOnly: return 'Jewellery';
      default: return 'All';
    }
  }
}
